import { Component, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ConsultationService } from '../../services/consultation.service';
import { PatientService } from '../../services/patient.service';
import { PrescriptionService } from '../../services/prescription.service';
import { AppointmentService } from '../../services/appointment.service';
import { Consultation, Symptom, ConsultationQuestion } from '../../models/consultation.model';
import { Patient } from '../../models/patient.model';
import { ButtonComponent, InputComponent } from '../ui';
import { ErrorService } from '../../shared/services/error.service';
import jsPDF from 'jspdf';

type Vitals = {
  bloodPressure?: string;
  heartRate?: number;
  respiratoryRate?: number;
  temperature?: number;
  spo2?: number;
  weight?: number;
};

@Component({
  selector: 'app-consultation',
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: './consultation.component.html',
  styleUrl: './consultation.component.css'
})
export class ConsultationComponent {
  patientId = signal<string>('');
  patient = signal<Patient | null>(null);
  patientName = signal('Patient');
  patientAge = signal('');
  selectedSymptoms = signal<Symptom[]>([]);
  selectedQuestions = signal<{ questionId: string; answer: any }[]>([]);
  familyHistory = signal<{ condition: string; relation: string; notes?: string }[]>([]);
  suggestedPrescriptions = signal<any[]>([]);
  selectedPrescriptions = signal<any[]>([]);
  chiefComplaint = signal('');
  diagnosis = signal<string[]>([]);
  prescriptionNotes = signal('');
  doctorNotes = signal('');
  vitals = signal<Vitals>({
    bloodPressure: '',
    heartRate: undefined as number | undefined,
    respiratoryRate: undefined as number | undefined,
    temperature: undefined as number | undefined,
    spo2: undefined as number | undefined,
    weight: undefined as number | undefined
  });
  investigations = signal<{ name: string; status: 'Pending' | 'Ordered' | 'Completed'; notes?: string }[]>([]);
  followUpEnabled = signal(true);
  followUpDate = signal(this.getDefaultFollowUpDate());
  followUpTime = signal('10:00');
  followUpNotes = signal('');
  
  availableSymptoms = signal<Symptom[]>([]);
  suggestedQuestions = signal<ConsultationQuestion[]>([]);
  showQuestionModal = signal(false);
  currentQuestion = signal<ConsultationQuestion | null>(null);
  currentAnswer = signal<any>(null);

  private errorService = inject(ErrorService);

  constructor(
    private consultationService: ConsultationService,
    public patientService: PatientService,
    private prescriptionService: PrescriptionService,
    private appointmentService: AppointmentService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.availableSymptoms.set(this.consultationService.commonSymptoms);
    
    this.route.queryParams.subscribe(params => {
      if (params['patientId']) {
        this.patientId.set(params['patientId']);
        const patient = this.patientService.getPatientById(params['patientId']);
        if (patient) {
          this.patient.set(patient);
          this.patientName.set(`${patient.firstName} ${patient.lastName}`);
          this.patientAge.set(this.calculateAge(patient.dateOfBirth));
        }
      }
    });
  }

  private calculateAge(date: Date): string {
    const birth = new Date(date);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return `${age}y`;
  }

  private getDefaultFollowUpDate(): Date {
    const defaultDate = new Date();
    defaultDate.setDate(defaultDate.getDate() + 7);
    defaultDate.setHours(0, 0, 0, 0);
    return defaultDate;
  }

  updateFollowUpDate(value: string) {
    if (value) {
      const selected = new Date(value);
      selected.setHours(0, 0, 0, 0);
      this.followUpDate.set(selected);
    }
  }

  updateFollowUpTime(value: string) {
    this.followUpTime.set(value || '10:00');
  }

  updateFollowUpNotes(value: string) {
    this.followUpNotes.set(value);
  }

  updateVital(field: keyof Vitals, value: string) {
    const current = this.vitals();
    const numericFields: (keyof Vitals)[] = ['heartRate', 'respiratoryRate', 'temperature', 'spo2', 'weight'];
    if (numericFields.includes(field)) {
      const parsed = value ? Number(value) : undefined;
      const updatedValue = parsed !== undefined && !Number.isNaN(parsed) ? parsed : undefined;
      this.vitals.set({ ...current, [field]: updatedValue } as Vitals);
    } else {
      this.vitals.set({ ...current, [field]: value } as Vitals);
    }
  }

  updateBloodPressure(part: 'systolic' | 'diastolic', value: string) {
    const current = this.vitals();
    const [sys = '', dia = ''] = (current.bloodPressure || '').split('/');
    const nextSys = part === 'systolic' ? value.trim() : sys.trim();
    const nextDia = part === 'diastolic' ? value.trim() : dia.trim();
    const formatted = [nextSys, nextDia].filter(v => v.length > 0).join('/');
    this.vitals.set({ ...current, bloodPressure: formatted } as Vitals);
  }

  addInvestigation() {
    this.investigations.set([
      ...this.investigations(),
      { name: '', status: 'Pending', notes: '' }
    ]);
  }

  updateInvestigationField(index: number, field: 'name' | 'notes', value: string) {
    const list = this.investigations();
    if (list[index]) {
      list[index] = { ...list[index], [field]: value };
      this.investigations.set([...list]);
    }
  }

  updateInvestigationStatus(index: number, status: 'Pending' | 'Ordered' | 'Completed') {
    const list = this.investigations();
    if (list[index]) {
      list[index] = { ...list[index], status };
      this.investigations.set([...list]);
    }
  }

  removeInvestigation(index: number) {
    const list = this.investigations();
    list.splice(index, 1);
    this.investigations.set([...list]);
  }

  private formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
  }

  private generateConsultationPdf(consultation: Consultation) {
    const doc = new jsPDF();
    const lineHeight = 7;
    let cursorY = 20;

    doc.setFontSize(16);
    doc.text('Consultation Summary', 14, cursorY);
    cursorY += lineHeight * 2;

    doc.setFontSize(12);
    doc.text(`Patient: ${consultation.patientName}`, 14, cursorY);
    cursorY += lineHeight;
    doc.text(`Doctor: ${consultation.doctorName}`, 14, cursorY);
    cursorY += lineHeight;
    doc.text(`Date: ${this.formatDate(new Date(consultation.date))}`, 14, cursorY);
    cursorY += lineHeight * 1.5;

    doc.setFont('helvetica', 'bold');
    doc.text('Chief Complaint', 14, cursorY);
    doc.setFont('helvetica', 'normal');
    cursorY += lineHeight;
    doc.text(doc.splitTextToSize(consultation.chiefComplaint || 'N/A', 180), 14, cursorY);
    cursorY += lineHeight * 1.5;

    doc.setFont('helvetica', 'bold');
    doc.text('Symptoms', 14, cursorY);
    doc.setFont('helvetica', 'normal');
    cursorY += lineHeight;
    consultation.symptoms.forEach(symptom => {
      doc.text(`• ${symptom.name} (${symptom.category}${symptom.severity ? `, ${symptom.severity}` : ''})`, 14, cursorY);
      cursorY += lineHeight;
    });
    cursorY += lineHeight * 0.5;

    if (consultation.vitals) {
      doc.setFont('helvetica', 'bold');
      doc.text('Vitals', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      const { bloodPressure, heartRate, respiratoryRate, temperature, spo2, weight } = consultation.vitals;
      const vitalsLines = [
        bloodPressure ? `Blood Pressure: ${bloodPressure}` : '',
        heartRate ? `Heart Rate: ${heartRate} bpm` : '',
        respiratoryRate ? `Respiratory Rate: ${respiratoryRate} breaths/min` : '',
        temperature ? `Temperature: ${temperature} °C` : '',
        spo2 ? `SpO₂: ${spo2}%` : '',
        weight ? `Weight: ${weight} kg` : ''
      ].filter(Boolean);
      vitalsLines.forEach(line => {
        doc.text(`• ${line}`, 14, cursorY);
        cursorY += lineHeight;
      });
      cursorY += lineHeight * 0.5;
    }

    if (consultation.investigations && consultation.investigations.length) {
      doc.setFont('helvetica', 'bold');
      doc.text('Investigations', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      consultation.investigations.forEach(investigation => {
        doc.text(`• ${investigation.name} (${investigation.status})${investigation.notes ? ` - ${investigation.notes}` : ''}`, 14, cursorY);
        cursorY += lineHeight;
      });
      cursorY += lineHeight * 0.5;
    }

    if (consultation.diagnosis.length) {
      doc.setFont('helvetica', 'bold');
      doc.text('Diagnosis', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      consultation.diagnosis.forEach(item => {
        doc.text(`• ${item}`, 14, cursorY);
        cursorY += lineHeight;
      });
      cursorY += lineHeight * 0.5;
    }

    if (consultation.suggestedPrescriptions.length) {
      doc.setFont('helvetica', 'bold');
      doc.text('Prescriptions', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      consultation.suggestedPrescriptions.forEach(prescription => {
        doc.text(`• ${prescription.medication} - ${prescription.dosage}`, 14, cursorY);
        cursorY += lineHeight;
        doc.text(`  ${prescription.frequency} • ${prescription.duration}`, 14, cursorY);
        cursorY += lineHeight;
        if (prescription.reason) {
          doc.text(`  Notes: ${prescription.reason}`, 14, cursorY);
          cursorY += lineHeight;
        }
      });
      cursorY += lineHeight * 0.5;
    }

    if (consultation.familyHistory.length) {
      doc.setFont('helvetica', 'bold');
      doc.text('Family History', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      consultation.familyHistory.forEach(history => {
        doc.text(`• ${history.condition} (${history.relation})${history.notes ? ` - ${history.notes}` : ''}`, 14, cursorY);
        cursorY += lineHeight;
      });
      cursorY += lineHeight * 0.5;
    }

    if (consultation.prescriptionNotes) {
      doc.setFont('helvetica', 'bold');
      doc.text('Prescription Notes', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      const notesLines = doc.splitTextToSize(consultation.prescriptionNotes, 180);
      doc.text(notesLines, 14, cursorY);
      cursorY += lineHeight * notesLines.length + lineHeight * 0.5;
    }

    if (consultation.doctorNotes) {
      doc.setFont('helvetica', 'bold');
      doc.text('Doctor Notes', 14, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += lineHeight;
      const docNotesLines = doc.splitTextToSize(consultation.doctorNotes, 180);
      doc.text(docNotesLines, 14, cursorY);
      cursorY += lineHeight * docNotesLines.length;
    }

    const fileName = `consultation-${consultation.patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  }

  previewPdf() {
    if (!this.patientId()) {
      this.errorService.showWarning('Select a patient first');
      return;
    }

    const consultation: Consultation = {
      id: 'preview',
      patientId: this.patientId()!,
      patientName: this.patientName(),
      doctorId: '1',
      doctorName: 'Dr. Sarah Williams',
      date: new Date(),
      chiefComplaint: this.chiefComplaint(),
      symptoms: this.selectedSymptoms(),
      vitals: this.vitals(),
      investigations: this.investigations(),
      selectedQuestions: this.selectedQuestions(),
      familyHistory: this.familyHistory(),
      suggestedPrescriptions: this.selectedPrescriptions(),
      diagnosis: this.diagnosis(),
      prescriptionNotes: this.prescriptionNotes(),
      doctorNotes: this.doctorNotes(),
      status: 'In Progress',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    this.generateConsultationPdf(consultation);
  }

  onSymptomSelect(symptom: Symptom) {
    const current = this.selectedSymptoms();
    if (!current.find(s => s.id === symptom.id)) {
      const newSymptom = { ...symptom, severity: 'Moderate' as const };
      this.selectedSymptoms.set([...current, newSymptom]);
      this.updateSuggestions();
    }
  }

  removeSymptom(symptomId: string) {
    this.selectedSymptoms.set(
      this.selectedSymptoms().filter(s => s.id !== symptomId)
    );
    this.updateSuggestions();
  }

  isSymptomSelected(symptomId: string): boolean {
    return !!this.selectedSymptoms().find(s => s.id === symptomId);
  }

  isQuestionAnswered(questionId: string): boolean {
    return !!this.selectedQuestions().find(q => q.questionId === questionId);
  }

  isPrescriptionSelected(medication: string): boolean {
    return !!this.selectedPrescriptions().find(p => p.medication === medication);
  }

  updateSuggestions() {
    const symptoms = this.selectedSymptoms();
    this.suggestedQuestions.set(this.consultationService.getSuggestedQuestions(symptoms));
    this.suggestedPrescriptions.set(this.consultationService.getPrescriptionSuggestions(symptoms));
  }

  openQuestion(question: ConsultationQuestion) {
    this.currentQuestion.set(question);
    const existing = this.selectedQuestions().find(q => q.questionId === question.id);
    this.currentAnswer.set(existing?.answer || null);
    this.showQuestionModal.set(true);
  }

  saveQuestionAnswer() {
    const question = this.currentQuestion();
    const answer = this.currentAnswer();
    if (question && answer !== null) {
      const current = this.selectedQuestions();
      const existing = current.findIndex(q => q.questionId === question.id);
      if (existing >= 0) {
        current[existing].answer = answer;
      } else {
        current.push({ questionId: question.id, answer });
      }
      this.selectedQuestions.set([...current]);
      this.showQuestionModal.set(false);
    }
  }

  addFamilyHistory() {
    this.familyHistory.set([
      ...this.familyHistory(),
      { condition: '', relation: '' }
    ]);
  }

  updateFamilyHistory(index: number, field: 'condition' | 'relation' | 'notes', value: string) {
    const current = this.familyHistory();
    if (current[index]) {
      current[index] = { ...current[index], [field]: value };
      this.familyHistory.set([...current]);
    }
  }

  removeFamilyHistory(index: number) {
    const current = this.familyHistory();
    current.splice(index, 1);
    this.familyHistory.set([...current]);
  }

  togglePrescription(prescription: any) {
    const current = this.selectedPrescriptions();
    const index = current.findIndex(p => p.medication === prescription.medication);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push({ ...prescription });
    }
    this.selectedPrescriptions.set([...current]);
  }

  addDiagnosis() {
    const diagnosis = prompt('Enter diagnosis:');
    if (diagnosis) {
      this.diagnosis.set([...this.diagnosis(), diagnosis]);
    }
  }

  removeDiagnosis(index: number) {
    const current = this.diagnosis();
    current.splice(index, 1);
    this.diagnosis.set([...current]);
  }

  saveConsultation() {
    if (!this.patientId() || this.selectedSymptoms().length === 0) {
      this.errorService.showWarning('Please select a patient and at least one symptom');
      return;
    }

    const consultation: Omit<Consultation, 'id' | 'createdAt' | 'updatedAt'> = {
      patientId: this.patientId()!,
      patientName: this.patientName(),
      doctorId: '1',
      doctorName: 'Dr. Sarah Williams',
      date: new Date(),
      chiefComplaint: this.chiefComplaint(),
      symptoms: this.selectedSymptoms(),
      vitals: this.vitals(),
      investigations: this.investigations(),
      selectedQuestions: this.selectedQuestions(),
      familyHistory: this.familyHistory(),
      suggestedPrescriptions: this.selectedPrescriptions(),
      diagnosis: this.diagnosis(),
      prescriptionNotes: this.prescriptionNotes(),
      doctorNotes: this.doctorNotes(),
      status: 'Completed'
    };

    const savedConsultation = this.consultationService.addConsultation(consultation);

    // Create prescription if medications selected
    if (this.selectedPrescriptions().length > 0) {
      this.prescriptionService.addPrescription({
        patientId: this.patientId()!,
        patientName: consultation.patientName,
        doctorId: '1',
        doctorName: 'Dr. Sarah Williams',
        date: new Date(),
        medications: this.selectedPrescriptions().map(p => ({
          name: p.medication,
          dosage: p.dosage,
          frequency: p.frequency,
          duration: p.duration,
          quantity: 30,
          instructions: p.reason
        })),
        status: 'Active'
      });
    }

    // Generate PDF summary
    this.generateConsultationPdf(savedConsultation);

    // Auto-schedule follow-up appointment
    if (this.followUpEnabled()) {
      const followUpDate = new Date(this.followUpDate());
      const [hours, minutes] = this.followUpTime().split(':').map(Number);
      followUpDate.setHours(hours ?? 10, minutes ?? 0, 0, 0);

      this.appointmentService.addAppointment({
        patientId: this.patientId()!,
        patientName: consultation.patientName,
        doctorId: '1',
        doctorName: 'Dr. Sarah Williams',
        date: followUpDate,
        time: this.followUpTime(),
        duration: 30,
        type: 'Follow-up',
        status: 'Scheduled',
      reason: this.followUpNotes() || `Follow-up for ${this.diagnosis().join(', ') || 'recent consultation'}`
      });
    }

    this.errorService.showSuccess('Consultation saved successfully!');
    this.router.navigate(['/medical-records']);
  }
}

